import {curry} from "@k/global/functionalTools";
import gsap from "gsap";

export class KeyFrameTargetValues {
    public open: AnimationTargetValue;
    public middle: AnimationTargetValue | null;
    public closed: AnimationTargetValue;

    constructor(open: AnimationTargetValue, closed: AnimationTargetValue, middle: AnimationTargetValue | null = null) {
        this.middle = middle;
        this.open = open;
        this.closed = closed;
    }

    public getTarget(openStatus: KEYFRAMETARGETS) {
        switch (openStatus) {
            case KEYFRAMETARGETS.CLOSED:
                return this.closed;
            case KEYFRAMETARGETS.MIDDLE:
                return this.middle;
            case KEYFRAMETARGETS.OPEN:
                return this.open;
        }
    }
}

export enum KEYFRAMETARGETS {
    "CLOSED",
    "OPEN",
    "MIDDLE"
}
export type AnimationTargetValue = number | { targetValue, opacity } | { targetValue, delay }
export const CLOSED = KEYFRAMETARGETS.CLOSED;
export const OPEN = KEYFRAMETARGETS.OPEN;
export const MIDDLE = KEYFRAMETARGETS.MIDDLE;



export const  tabAnimationRunning = ()=>{
    return gsap.globalTimeline.getChildren(true, true, false).filter(c => c.isActive()).length>0;
}



export abstract class AnimationBase {

    abstract getAnimationObjects();

    private timeline = gsap.timeline({autoRemoveChildren:true});

    public animate(selector:string) {
        this.timeline.clear();
        this.getAnimationObjects().forEach(o=>this.timeline.to(selector, o));
    }
}

export class UpDownAnimation extends AnimationBase{

    protected readonly targetValue;
    protected readonly duration: number;

    constructor(duration, targetValue) {
        super();
        this.duration = duration;
        this.targetValue = targetValue;
    }
    getAnimationObjects() {
        return [{duration:this.duration, y:this.targetValue}];
    }
}

export class UpDownAnimationWithDelay extends UpDownAnimation {
    private readonly delay:number;

    constructor(duration, targetvalue, delay) {
        super(duration, targetvalue);
        this.delay = delay;
    }

    getAnimationObjects(): [{ duration: number; y: number, delay:number }] {
        return [{duration:this.duration, y:this.targetValue, delay:this.delay}];
    }
}

export class UpDownAnimationPercent extends AnimationBase{

    private readonly targetValue;
    private readonly duration: number;

    constructor(duration, targetValue) {
        super();
        this.duration = duration;
        this.targetValue = targetValue;
    }
    getAnimationObjects() {
        return [{duration:this.duration, yPercent:this.targetValue}];
    }
}

export class AutoAlphaAnimation extends AnimationBase {
    private readonly targetValue;

    public constructor(targetValue) {
        super();
        this.targetValue =targetValue;

    }

    getAnimationObjects() {
        return [{duration:0.4, autoAlpha:this.targetValue}]
    }

}

export class UpDownAnimationWithCalculation extends AnimationBase {
    private readonly pixelToSubtactFromFullSize;
    private readonly duration: number;

    constructor(duration, pixelToSubtactFromFullSize) {
        super();
        this.duration = duration;
        this.pixelToSubtactFromFullSize = pixelToSubtactFromFullSize;
    }
    getAnimationObjects() {
        //return [{duration:this.duration, y:"calc(-100%+"+this.pixelToSubtactFromFullSize+"px)"}];
        return [{duration:this.duration, yPercent: this.pixelToSubtactFromFullSize}];
    }
}


export class LeftRightAnimationPercent extends AnimationBase {
    protected readonly targetValueLeftRight:number;
    protected readonly duration:number;

    constructor(duration, targetValueLeftRight) {
        super();
        this.duration = duration;
        this.targetValueLeftRight = targetValueLeftRight;
    }
    getAnimationObjects() {
        return [{duration:this.duration, xPercent:this.targetValueLeftRight}];
    }
}

export class LeftRightAnimationpercentWithOpacity extends LeftRightAnimationPercent {
    private readonly targetValueOpacity:number;

    constructor(duration, targetValueLeftRight, targetValueOpacity) {
        super(duration, targetValueLeftRight);
        this.targetValueOpacity = targetValueOpacity;
    }

    getAnimationObjects() {
        return [{duration:this.duration, opacity:this.targetValueOpacity, xPercent:this.targetValueLeftRight}];
    }
}

export class FadeInFromBehindAnimation extends AnimationBase {
    private readonly zIndexDuration;
    private readonly zIndexTarget;
    private readonly opacityDuration;
    private readonly opacityTarget;

    public constructor(zIndexDuration, zIndexTarget, opacityDuration, opacityValue) {
        super();
        this.zIndexDuration = zIndexDuration;
        this.zIndexTarget = zIndexTarget;
        this.opacityDuration = opacityDuration;
        this.opacityTarget = opacityValue;
    }

    getAnimationObjects() {
        return [{duration:this.zIndexDuration, zIndex:this.zIndexTarget}, {duration: this.opacityDuration, opacity:this.opacityTarget}];
    }
}

export class AnimationWaitingForTargetFactory {

    private static animate(selector, animationFactory: ((targetValue: number | { zIndex, opacity }) => AnimationBase), targetValue) {
        const o = animationFactory(targetValue);
        o.animate(selector);
    }

    public static getAnimation = (selector: string, animationFactory: (targetValue: number | { targetValue, opacity } | {targetValue, delay}) => AnimationBase) => {
        return curry(AnimationWaitingForTargetFactory.animate, selector, animationFactory);
    }


    public static getUpDownAnimation(selector, duration = 0.5) {
        return  AnimationWaitingForTargetFactory.getAnimation(selector,target => new UpDownAnimation(duration, target));
    }

    public static getLeftRightAnimationpercentWithOpacityAnimation(selector) {
        return AnimationWaitingForTargetFactory.getAnimation(selector,
            target=>
                new LeftRightAnimationpercentWithOpacity(0.5, (target as {targetValue, opacity}).targetValue, (target as {targetValue, opacity}).opacity)
        );
    }

    public static getFadeInOutAnimation(selector) {
        return AnimationWaitingForTargetFactory.getAnimation(selector,
            target=>new FadeInFromBehindAnimation(
                0.1, (target as {targetValue, opacity}).targetValue,
                0.5, (target as {targetValue, opacity}).opacity)
        )
    }

    public static getUpDownPercentageAnimation(selector, duration = 0.5) {
        return AnimationWaitingForTargetFactory.getAnimation(selector, target=>new UpDownAnimationPercent(duration, target))
    }
}
